import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useCheckbox } from '@react-aria/checkbox';
import { useFocusRing } from '@react-aria/focus';
import { IoIosCheckboxOutline } from '@react-icons/all-files/io/IoIosCheckboxOutline';
import { MdCheckBox } from '@react-icons/all-files/md/MdCheckBox';
import { useToggleState } from '@react-stately/toggle';
import { CheckboxProps } from '@react-types/checkbox';
import cls from 'clsx';
import { JSX } from 'react';
import { ReactNode, MutableRefObject, forwardRef } from 'react';
import { useEnsuredForwardedRef } from 'react-use';
import {
  LabelButton,
  LabelButtonProps,
} from '@/components/atoms/Button/Button';
import styles from './Checkbox.module.scss';

interface Props extends Omit<CheckboxProps, 'value'> {
  children: ReactNode;
  className?: string;
  'aria-label': string;
  value: boolean;
  onClick?: () => void;
  labelComponent?: (params: CheckboxInputLabelProps) => JSX.Element;
  variant?: LabelButtonProps['variant'];
}

interface CheckboxInputLabelProps {
  isSelected: boolean;
  isFocusVisible: boolean;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  variant: LabelButtonProps['variant'];
}

const DefaultLabelComponent = ({
  isSelected,
  isFocusVisible,
  className,
  children,
  onClick,
  variant,
}: CheckboxInputLabelProps) => (
  <LabelButton
    variant={variant}
    color={isSelected ? 'primary' : 'gray'}
    disableHover={isSelected}
    data-test-id="checkbox-button"
    id={styles.label}
    className={cls(
      { [styles.selected]: isSelected },
      { [styles.focus]: isFocusVisible },
      className
    )}
    onClick={onClick}
  >
    {!isSelected ? (
      <IoIosCheckboxOutline
        className={styles.icon}
        id={styles.notSelected}
        size={30}
      />
    ) : (
      <MdCheckBox className={styles.icon} size={30} />
    )}
    {children}
  </LabelButton>
);

export const Checkbox = forwardRef<HTMLInputElement, Props>(
  function CheckBoxBase(
    {
      children,
      onClick,
      className,
      value,
      labelComponent,
      variant = 'icon',
      ...rest
    },
    ref
  ) {
    const state = useToggleState({ isSelected: value, ...rest });
    const radioInputRef = useEnsuredForwardedRef(
      ref as MutableRefObject<HTMLInputElement>
    );
    const { inputProps } = useCheckbox(rest, state, radioInputRef);
    const { isFocusVisible, focusProps } = useFocusRing();

    const WrappingLabelComponent = labelComponent ?? DefaultLabelComponent;

    return (
      <WrappingLabelComponent
        variant={variant}
        onClick={onClick}
        isSelected={state.isSelected}
        isFocusVisible={isFocusVisible}
        className={className}
      >
        <VisuallyHidden>
          <input {...inputProps} {...focusProps} ref={ref} />
        </VisuallyHidden>
        {children}
      </WrappingLabelComponent>
    );
  }
);
